import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { initAppleIAPListener } from "@/lib/appleIap";
import { initGoogleBillingReconcile } from "@/lib/googleBilling";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LoadoutProvider } from "@/context/LoadoutContext";
import { I18nProvider } from "@/i18n/I18nContext";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import LevelUpWatcher from "@/components/LevelUpWatcher";
import OfflineMealSync from "@/components/OfflineMealSync";
import Mascot from "@/components/Mascot";
import BetaConsentModal from "@/components/BetaConsentModal";
import UpgradeLimitDialog from "@/components/UpgradeLimitDialog";
import SwipeHome from "@/components/SwipeHome";
import NativeReminderSync from "@/components/NativeReminderSync";
import DeepLinkListener from "@/components/DeepLinkListener";
import OnboardingGate from "@/components/OnboardingGate";
import { initHapticToasts } from "@/lib/haptics";
import { api, setAuthToken } from "@/lib/http";
import { Capacitor } from "@capacitor/core";
import MobileOnlyNotice from "@/components/MobileOnlyNotice";

const IS_NATIVE_APP = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();

import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import ClientDashboard from "@/pages/client/Dashboard";
// Everything else is code-split: smaller first bundle → faster cold start on phones.
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPassword"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPassword"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const NutritionistDashboard = lazy(() => import("@/pages/nutritionist/Dashboard"));
const NutritionistAppStats = lazy(() => import("@/pages/nutritionist/AppStats"));
const ClientsList = lazy(() => import("@/pages/nutritionist/ClientsList"));
const ClientDetail = lazy(() => import("@/pages/nutritionist/ClientDetail"));
const MealPlanBuilder = lazy(() => import("@/pages/nutritionist/MealPlanBuilder"));
const NutAppointments = lazy(() => import("@/pages/nutritionist/Appointments"));
const NutritionistProfile = lazy(() => import("@/pages/nutritionist/Profile"));
const ClientProgress = lazy(() => import("@/pages/client/Progress"));
const ClientFriends = lazy(() => import("@/pages/client/Friends"));
const ClientWorkouts = lazy(() => import("@/pages/client/Workouts"));
const ClientPantry = lazy(() => import("@/pages/client/Pantry"));
const ShoppingCheck = lazy(() => import("@/pages/client/ShoppingCheck"));
const ClientWeeklyMenu = lazy(() => import("@/pages/client/WeeklyMenu"));
const ClientDiary = lazy(() => import("@/pages/client/Diary"));
const AddMealPage = lazy(() => import("@/pages/client/AddMeal"));
const ClientProfile = lazy(() => import("@/pages/client/Profile"));
const EveningReport = lazy(() => import("@/pages/client/EveningReport"));
const ClientRestaurant = lazy(() => import("@/pages/client/Restaurant"));
const PlateSolanoPage = lazy(() => import("@/pages/client/PlateSolano"));
const RewardsPage = lazy(() => import("@/pages/client/Rewards"));
const ProUpgrade = lazy(() => import("@/pages/client/ProUpgrade"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const ChatPage = lazy(() => import("@/pages/Chat"));
const RecipesPage = lazy(() => import("@/pages/Recipes"));
const NutritionistFeed = lazy(() => import("@/pages/nutritionist/Feed"));
const AdminPanel = lazy(() => import("@/pages/admin/AdminPanel"));
const PrivacyLegal = lazy(() => import("@/pages/PrivacyLegal"));
const DeleteAccountInfo = lazy(() => import("@/pages/DeleteAccountInfo"));

// Shown while a lazily-loaded route chunk downloads.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
      <div className="h-8 w-8 rounded-full border-2 border-[#5A8B8F]/30 border-t-[#5A8B8F] animate-spin" />
    </div>
  );
}

// Super-admins have ONE admin area: the coach dashboard redirects to /admin.
function CoachHome() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/admin" replace />;
  return <NutritionistDashboard />;
}

const homeFor = (role) => (role === "super_admin" ? "/admin" : role === "nutritionist" || role === "coach" ? "/nutrizionista" : "/app");


import "@/App.css";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  // iOS Chrome/Safari very reliably double-fires this effect (bfcache, focus
  // reload, StrictMode). The Emergent session_id is single-use, so a naive
  // second call fails with 401 and the user silently bounces back to /login.
  // We guard the exchange with a ref + a per-tab sessionStorage entry so no
  // matter how many effects re-run, we run the network call once.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) { navigate("/login"); return; }

    // Immediately wipe the hash so a bfcache/back-forward restore can't
    // re-trigger the callback (and cause a double exchange).
    window.history.replaceState({}, document.title, "/");

    // Cross-tab guard: if this exact session_id was already redeemed on this
    // device in the last 60 s, skip the network call and just re-check /me.
    const inflightKey = `auth:exchange:${sessionId}`;
    const inflight = sessionStorage.getItem(inflightKey);
    if (inflight) {
      (async () => {
        try {
          const { data: me } = await api.get("/auth/me");
          setUser(me);
          navigate(!me.role ? "/onboarding" : homeFor(me.role), { replace: true });
        } catch {
          setError("Errore di autenticazione. Riprova.");
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        }
      })();
      return;
    }
    sessionStorage.setItem(inflightKey, String(Date.now()));
    setTimeout(() => sessionStorage.removeItem(inflightKey), 60000);

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });
        if (data.token) setAuthToken(data.token);
        setUser(data.user);
        if (!data.user.role) navigate("/onboarding", { replace: true });
        else navigate(homeFor(data.user.role), { replace: true });
      } catch (e) {
        // Session may already have been exchanged (by a previous tab / effect
        // run). Fall back to /auth/me — if there's already a valid cookie/token
        // the user is authenticated and we can continue silently.
        try {
          const { data: me } = await api.get("/auth/me");
          setUser(me);
          navigate(!me.role ? "/onboarding" : homeFor(me.role), { replace: true });
        } catch {
          setError("Errore di autenticazione. Riprova.");
          setTimeout(() => navigate("/login", { replace: true }), 1500);
        }
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center flex flex-col items-center">
        <Mascot size={128} pose="bounce" />
        <p className="mt-4 text-sm text-slate-600" data-testid="auth-callback-msg">{error || "Accesso in corso…"}</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, role, allowNoRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <Mascot size={112} pose="bounce" />
    </div>
  );
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!user.role && !allowNoRole) return <Navigate to="/onboarding" replace />;
  if (user.role && allowNoRole && user.role !== "client") {
    // Non-client roles never need the onboarding page
    return <Navigate to={homeFor(user.role)} replace />;
  }
  const roleOk = !role || user.role === role ||
    (role === "nutritionist" && (user.role === "super_admin" || user.role === "coach"));
  if (!roleOk) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  // Web gate: the CLIENT app is exclusive to the native iPhone app. In a
  // browser, client accounts only see a "please use the app" notice —
  // admin & coach areas remain fully usable on the web. The dev server
  // (preview) is exempt so client flows stay testable.
  if (user.role === "client" && !IS_NATIVE_APP && process.env.NODE_ENV === "production") {
    return <MobileOnlyNotice />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Read a fresh Cache-API deeplink (SW-persisted) once during the initial
  // render — before RootRedirect commits to /app or /nutrizionista. This
  // handles iOS PWA cold-start where the ?deeplink= query is stripped.
  const [cacheDeeplink, setCacheDeeplink] = useState(undefined);   // undefined = still loading, null = none
  useEffect(() => {
    if (typeof caches === "undefined") { setCacheDeeplink(null); return; }
    let alive = true;
    (async () => {
      try {
        const dl = await caches.open("solano-pending-deeplink");
        const resp = await dl.match("/__deeplink__");
        if (!resp) { if (alive) setCacheDeeplink(null); return; }
        await dl.delete("/__deeplink__");
        const payload = await resp.json().catch(() => null);
        const url = payload?.url;
        const ts = payload?.ts;
        const fresh = typeof url === "string" && url.startsWith("/") &&
                      typeof ts === "number" && Date.now() - ts < 30_000;
        if (alive) setCacheDeeplink(fresh ? url : null);
      } catch { if (alive) setCacheDeeplink(null); }
    })();
    return () => { alive = false; };
  }, []);
  if (loading || cacheDeeplink === undefined) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <Mascot size={112} pose="bounce" />
    </div>
  );
  // Deep-link preservation on cold-start. iOS PWA installed from the
  // home screen launches at manifest `start_url` = "/" and ignores
  // whatever URL the SW passed to `openWindow()`. The SW compensates
  // by putting the target in a `?deeplink=` param AND in the Cache API
  // — we honour whichever survives here.
  const sp = new URLSearchParams(location.search);
  const deeplink = sp.get("deeplink") || cacheDeeplink;
  if (deeplink && deeplink.startsWith("/")) {
    if (!user) return <Navigate to={`/login?next=${encodeURIComponent(deeplink)}`} replace />;
    if (!user.role) return <Navigate to="/onboarding" replace />;
    return <Navigate to={deeplink} replace />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/onboarding" replace />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function CrashTest() { throw new Error("Crash test (dev only)"); }

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Service-worker → app router bridge. When the user taps a push
  // notification (e.g. a supplement reminder) the SW postMessages
  // `{type: "solano:navigate", url: "/app/profilo#supplements"}` to the
  // active window. Without this listener the SW message was a dead
  // letter — the PWA came to foreground but stayed on whatever page the
  // user last had open, defeating the whole point of deep-linking from
  // the notification. We hydrate the incoming URL into react-router so
  // Profile's hashchange handler auto-expands the right section too.
  //
  // MUST be defined before any early-return in this component or React
  // will refuse to render (hooks-order rule).
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const onMsg = (ev) => {
      const d = ev?.data;
      if (!d || d.type !== "solano:navigate" || !d.url) return;
      const target = String(d.url);
      if (!target.startsWith("/")) return;
      const [pathAndSearch, hash] = target.split("#");
      const currentFull = window.location.pathname + window.location.search;
      if (currentFull === pathAndSearch && hash) {
        window.history.replaceState({}, "", pathAndSearch);
        setTimeout(() => { window.location.hash = hash; }, 0);
      } else {
        navigate(target);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, [navigate]);

  // Cold-start deep-link (Cache-API path). Complements the ?deeplink= drainer
  // below: iOS PWA can strip the query on cold start, so the service worker
  // also persists the target URL to a well-known Cache API entry. Drain
  // that entry here on boot so the app routes to the tapped notification
  // even when the query didn't survive. Feb 2026 bug: supplement pushes
  // opened the Dashboard instead of the supplement page on iOS.
  useEffect(() => {
    if (typeof caches === "undefined") return;
    let cancelled = false;
    (async () => {
      try {
        const dl = await caches.open("solano-pending-deeplink");
        const resp = await dl.match("/__deeplink__");
        if (!resp) return;
        // Always drain — stale entries could otherwise re-route unrelated
        // navigations. We only ACT on it if it's fresh (< 30s).
        await dl.delete("/__deeplink__");
        if (cancelled) return;
        const payload = await resp.json().catch(() => null);
        if (!payload || typeof payload.url !== "string" || !payload.url.startsWith("/")) return;
        // If we're already at the target (query-drainer already ran) skip.
        const currentFull = location.pathname + location.search + location.hash;
        if (payload.url === currentFull) return;
        // Skip stale hand-offs so a lingering entry doesn't hijack navigation.
        if (typeof payload.ts !== "number" || Date.now() - payload.ts > 30_000) return;
        navigate(payload.url, { replace: true });
      } catch { /* silent — deeplink is best-effort */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cold-start deep-link. iOS PWA notoriously launches the installed app
  // at its manifest `start_url` and IGNORES the URL passed to the SW's
  // openWindow() on notification click. To route around that we make
  // the SW open a URL like `/app/profilo?deeplink=%2Fapp%2Fprofilo%23supplements`.
  // Here we drain that param and navigate to the encoded target ONCE.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const deeplink = sp.get("deeplink");
    if (!deeplink || !deeplink.startsWith("/")) return;
    sp.delete("deeplink");
    const cleaned = location.pathname + (sp.toString() ? `?${sp}` : "") + location.hash;
    window.history.replaceState({}, "", cleaned);
    if (deeplink === cleaned || deeplink === location.pathname + location.hash) {
      const h = deeplink.split("#")[1];
      if (h) setTimeout(() => { window.location.hash = h; }, 0);
    } else {
      navigate(deeplink, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <AppErrorBoundary pathname={location.pathname}>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {process.env.NODE_ENV !== "production" && <Route path="/__crash" element={<CrashTest />} />}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/onboarding" element={<ProtectedRoute allowNoRole><Onboarding /></ProtectedRoute>} />

      <Route path="/admin" element={<ProtectedRoute role="super_admin"><AdminPanel /></ProtectedRoute>} />
      <Route path="/nutrizionista" element={<ProtectedRoute role="nutritionist"><CoachHome /></ProtectedRoute>} />
      <Route path="/nutrizionista/statistiche" element={<ProtectedRoute role="nutritionist"><NutritionistAppStats /></ProtectedRoute>} />
      <Route path="/nutrizionista/feed" element={<ProtectedRoute role="nutritionist"><NutritionistFeed /></ProtectedRoute>} />
      <Route path="/nutrizionista/clienti" element={<ProtectedRoute role="nutritionist"><ClientsList /></ProtectedRoute>} />
      <Route path="/nutrizionista/clienti/:id" element={<ProtectedRoute role="nutritionist"><ClientDetail /></ProtectedRoute>} />
      <Route path="/nutrizionista/clienti/:id/piano" element={<ProtectedRoute role="nutritionist"><MealPlanBuilder /></ProtectedRoute>} />
      <Route path="/nutrizionista/appuntamenti" element={<ProtectedRoute role="nutritionist"><NutAppointments /></ProtectedRoute>} />
      <Route path="/nutrizionista/profilo" element={<ProtectedRoute role="nutritionist"><NutritionistProfile /></ProtectedRoute>} />
      <Route path="/nutrizionista/chat" element={<ProtectedRoute role="nutritionist"><ChatPage /></ProtectedRoute>} />
      <Route path="/nutrizionista/ricette" element={<ProtectedRoute role="nutritionist"><RecipesPage /></ProtectedRoute>} />

      <Route path="/app" element={<ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>} />
      <Route path="/app/progressi" element={<ProtectedRoute role="client"><ClientProgress /></ProtectedRoute>} />
      <Route path="/app/amici" element={<ProtectedRoute role="client"><ClientFriends /></ProtectedRoute>} />
      <Route path="/app/diario" element={<ProtectedRoute role="client"><ClientDiary /></ProtectedRoute>} />
      <Route path="/app/pasto" element={<ProtectedRoute role="client"><AddMealPage /></ProtectedRoute>} />
      <Route path="/app/ricette" element={<ProtectedRoute role="client"><RecipesPage /></ProtectedRoute>} />
      <Route path="/app/allenamenti" element={<ProtectedRoute role="client"><ClientWorkouts /></ProtectedRoute>} />
      <Route path="/app/dispensa" element={<ProtectedRoute role="client"><ClientPantry /></ProtectedRoute>} />
      <Route path="/app/einkauf" element={<ProtectedRoute role="client"><ShoppingCheck /></ProtectedRoute>} />
      <Route path="/app/menu-settimanale" element={<ProtectedRoute role="client"><ClientWeeklyMenu /></ProtectedRoute>} />
      <Route path="/app/profilo" element={<ProtectedRoute role="client"><ClientProfile /></ProtectedRoute>} />
      <Route path="/app/riepilogo-serale" element={<ProtectedRoute role="client"><EveningReport /></ProtectedRoute>} />
      <Route path="/app/ristorante" element={<ProtectedRoute role="client"><ClientRestaurant /></ProtectedRoute>} />
      <Route path="/app/piatto-solano" element={<ProtectedRoute role="client"><PlateSolanoPage /></ProtectedRoute>} />
      <Route path="/app/premi" element={<ProtectedRoute role="client"><RewardsPage /></ProtectedRoute>} />
      <Route path="/app/privacy" element={<ProtectedRoute><PrivacyLegal /></ProtectedRoute>} />
      <Route path="/nutrizionista/privacy" element={<ProtectedRoute><PrivacyLegal /></ProtectedRoute>} />
      <Route path="/privacy" element={<PrivacyLegal publicView />} />
      <Route path="/delete-account" element={<DeleteAccountInfo />} />
      <Route path="/datenschutz" element={<PrivacyLegal publicView />} />
      <Route path="/app/abo" element={<ProtectedRoute role="client"><ProUpgrade /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
    </Suspense>
    </AppErrorBoundary>
  );
}

export default function App() {
  useEffect(() => { initAppleIAPListener(); initGoogleBillingReconcile(); initHapticToasts(); }, []);
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <LoadoutProvider>
            <BrowserRouter>
              <SwipeHome />
              <NativeReminderSync />
              <DeepLinkListener />
              <OnboardingGate />
              <AppRouter />
              <BetaConsentModal />
              <UpgradeLimitDialog />
              <LevelUpWatcher />
              <OfflineMealSync />
              <Toaster
                position="top-right"
                richColors
                offset={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
                mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
              />
            </BrowserRouter>
          </LoadoutProvider>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
